// İSG uzmanı portalı — "Karar" bandının sahne/kuyruk durum motoru
// (`docs/reviews/S32b-gate-desk-relayout.md` §2 "the verdict is a property of the desk", §2.4).
//
// Option A'da verdict artık bir sekmenin İÇİNDE değil, sayfanın TEK her-zaman-görünür bandıdır —
// bu yüzden staging tek bir hook'a toplanır ve İKİ kaynaktan beslenir:
//
//   - `pushGateScans` — kapı kodu yoklamasından (`useGateCode.ts`, sıralı+tekilleştirilmiş HAM
//     `GateScanDto[]`). Kuyruğu ASLA KESMEZ: sahne doluysa arkaya eklenir (§2.4 kural 1: "One
//     verdict on the stage at a time... Not a feed"; bir okutma bir reddi ASLA yerinden ETMEZ).
//   - `pushTcResult` / `pushTcError` — GÜVENLİK'in kendi TC sorgusu (`TcLookupTab.tsx`). Rehber
//     eylemli olduğu için HER ZAMAN sahneyi KESER (§2.4 kural 6, YENİ): "the guard explicitly
//     pressed Sorgula; an unanswered explicit request is worse than a deferred denial" — kesilen
//     öğe kuyruğun BAŞINA döner (kaybolmaz, yalnız ertelenir), sayaç bunu YANSITIR.
//
// **Otomatik temizlenme YOK (owner canlı-kullanım bulgusu, S32b sonrası ikinci tur — S32/S32b
// §2.4 kural 3/4/5'in ÜSTÜNE geçer).** Guard kararı hâlâ OKURKEN kart kendini temizliyordu.
// ARTIK hiçbir varyant zaman-güdümlü temizlenmez — sahnedeki karar YALNIZ guard'ın kendi
// eylemiyle (`acknowledge()`/`advance`, ya da bir TC sorgusunun `interrupt()`'ü) ilerler.
// Bu iki şeyi eskisinden DAHA kritik kılar (her ikisi de bu turda ayrıca güçlendirildi):
//   - **Kuyruk artık PASİF bir sayı DEĞİL, bir İSTEM'dir** — `VerdictPanel.tsx`'in başlık
//     rozeti artık "bekleyen VAR, ilerletmek SENİN tıklamanla olur" diye OKUNUR (ton + ikon +
//     eylem-adı geçen metin).
//   - **Yaş satırı (`DenialAgeLine` → `VerdictAgeLine`, aşağıda `VerdictCard.tsx`) artık yalnız
//     `denied`'de DEĞİL, `allowed`'da da çıkar** — bir karar artık SÜRESİZ ekranda kalabildiği
//     için "bu hâlâ GÜNCEL mi" sorusunun cevabı salt görsel VARLIKLA değil, açık bir yaş
//     metniyle verilmek ZORUNDA (guard eski bir "GİRİŞ VAR"a bakıp güncelmiş gibi davranmasın).

import type { EntryVerifyResultDto, GateScanDto, TcLookupResultDto } from '@isgportal/shared';
import { useCallback, useRef, useState } from 'react';

export interface StagedGateVerdict {
  source: 'gate';
  key: string;
  scan: GateScanDto;
}

export interface StagedTcVerdict {
  source: 'tc';
  key: string;
  result: TcLookupResultDto;
  /** UTC ISO 8601 — İSTEMCİ ANINDA damgalanır (§29.5 TC yanıtı `scannedAt` TAŞIMAZ). */
  at: string;
}

/** Akış A (`ScanTab.tsx`, brief 092) — güvenlik personelin giriş QR'ını KAMERAYLA/elle okutur →
 *  `POST /security/entry/verify`. TC sorgusuyla AYNI disiplin: GUARD-güdümlü, sahneyi KESER. */
export interface StagedVerifyVerdict {
  source: 'verify';
  key: string;
  result: EntryVerifyResultDto;
  /** UTC ISO 8601 — istemci anı (§29.5 verify yanıtı `scannedAt` TAŞIMAZ, TC ile aynı). */
  at: string;
}

export interface StagedTcErrorVerdict {
  source: 'tcError';
  key: string;
  /** UTC ISO 8601 — istemci anı (yalnız sıralama/anahtar için, ekranda GÖSTERİLMEZ). */
  at: string;
}

export type StagedVerdict =
  | StagedGateVerdict
  | StagedTcVerdict
  | StagedVerifyVerdict
  | StagedTcErrorVerdict;

export interface UseVerdictStageResult {
  stage: StagedVerdict | null;
  queuedCount: number;
  /** S32d `docs/reviews/S32d-verdict-and-detail.md` §3.4/S32c §4.5 — kuyruğun BAŞINDAKİ öğe bir
   *  RET mi? `Sıradaki (N)` düğmesi bunu bir `OctagonX` ikonuyla önceden haber verir: auto-clear
   *  kalktığından bu düğme sahneyi ilerletebilen TEK guard eylemi ve N kez art arda tıklamak bir
   *  reddi OKUNMADAN geçebilir — o "delik" S32c'de TESPİT edilmiş, S32d ile KAPATILIYOR. */
  nextIsDenial: boolean;
  /** Sahnedeki kararı onaylar — kuyruktaki bir sonrakine geçer. Zaman-güdümlü hiçbir yol YOKTUR;
   *  bu, sahneyi ilerletebilen İKİ eylemden biridir (öteki `interrupt`, bir TC sorgusu). */
  acknowledge: () => void;
  /** Kapı kodu yoklamasından TAZE, ham okutmalar — sahneyi ASLA KESMEZ. */
  pushGateScans: (scans: GateScanDto[]) => void;
  /** TC sorgu sonucu — GUARD-GÜDÜMLÜ, sahneyi HER ZAMAN KESER (§2.4 kural 6). */
  pushTcResult: (result: TcLookupResultDto) => void;
  /** Personel QR doğrulama sonucu (Akış A, `ScanTab.tsx`) — GUARD-GÜDÜMLÜ (güvenlik kamerayı
   *  açıp okuttu / elle yapıştırdı), TC ile AYNI kural: sahneyi HER ZAMAN KESER. */
  pushVerifyResult: (result: EntryVerifyResultDto) => void;
  /** TC sorgusu / personel QR doğrulaması ağ hatası aldı — aynı kesme kuralı (guard zaten
   *  aktif bir eylem yaptı). İki guard-güdümlü yol da AYNI nötr "ağ hatası" kartına düşer. */
  pushTcError: () => void;
}

/** Kuyruğun/sahnenin BAŞINDAKİ öğe gerçek bir izin reddi mi — `Sıradaki (N)` düğmesinin
 *  `OctagonX` önizlemesi + `VerdictCard` ikonu için. Nötr durumlar (TC `found=false`, ağ hatası)
 *  ret DEĞİLDİR; `verify` yolunda her `allowed=false` (izin VE jeton aileleri) bir reddir. */
function isDenialEntry(entry: StagedVerdict): boolean {
  switch (entry.source) {
    case 'gate':
      return !entry.scan.allowed;
    case 'tc':
      return entry.result.found && !entry.result.allowed;
    case 'verify':
      return !entry.result.allowed;
    default:
      return false;
  }
}

export function useVerdictStage(): UseVerdictStageResult {
  const [stage, setStage] = useState<StagedVerdict | null>(null);
  const [queue, setQueue] = useState<StagedVerdict[]>([]);
  const stageRef = useRef<StagedVerdict | null>(null);
  const queueRef = useRef<StagedVerdict[]>([]);
  // TC girişleri için tekil anahtar sayacı — `Date.now()` tek başına aynı ms içinde ard arda iki
  // gönderimde çakışabilir (dedupe/React key çakışması), sayaç bunu yapısal olarak İMKÂNSIZ kılar.
  const tcSeqRef = useRef(0);

  // Not: `stageRef`/`queueRef` ARTIK yalnız pasif bir `useEffect`ten değil, ÜÇ mutator'ın
  // (`advance`, `pushGateScans`, `interrupt`) HER BİRİNDEN eşzamanlı yazılır — commit sonrası
  // çalışan bir "senkron" effect burada YOKTUR, bilerek kaldırıldı (code review bulgusu:
  // `advance` refi yazMIYORDU, iki kardeşi yazıyordu; boşluğu commit-sonrası bir pasif effect
  // "kapatıyordu" ve bu tam da bu böceği gizleyen şeydi). Üç yazıcı da senkronsa, pasif bir
  // "güvenlik ağı" effect'i yalnız ikinci bir gerçek kaynağı andırır — bu yüzden effect'ler
  // silindi, ref'ler artık render sırasında DEĞİL, olay anında yazılan TEK gerçek kaynaktır.

  // `queue`'ya BAĞIMLI DEĞİL, kalıcı olarak KARARLI kimlik (S32 review #2 ile AYNI desen,
  // `useGateCode.ts`'in eski `advance`'i) — `queueRef` üzerinden okuyarak bu callback'in kimliği
  // hiç DEĞİŞMEZ.
  const advance = useCallback(() => {
    const [next, ...rest] = queueRef.current;
    queueRef.current = rest;
    setQueue(rest);
    stageRef.current = next ?? null;
    setStage(next ?? null);
  }, []);

  const pushGateScans = useCallback((scans: GateScanDto[]) => {
    if (scans.length === 0) {
      return;
    }
    const entries: StagedGateVerdict[] = scans.map((scan) => ({
      source: 'gate',
      key: `gate|${scan.scannedAt}|${scan.personnelName}`,
      scan,
    }));
    if (stageRef.current == null) {
      const [first, ...rest] = entries;
      stageRef.current = first ?? null;
      setStage(first ?? null);
      if (rest.length > 0) {
        queueRef.current = [...queueRef.current, ...rest];
        setQueue((q) => [...q, ...rest]);
      }
    } else {
      queueRef.current = [...queueRef.current, ...entries];
      setQueue((q) => [...q, ...entries]);
    }
  }, []);

  // Guard-güdümlü kesme (§2.4 kural 6) — sahnedeki (varsa) öğe kuyruğun BAŞINA döner, kaybolmaz.
  const interrupt = useCallback((entry: StagedVerdict) => {
    const outgoing = stageRef.current;
    const nextQueue = outgoing ? [outgoing, ...queueRef.current] : queueRef.current;
    queueRef.current = nextQueue;
    setQueue(nextQueue);
    stageRef.current = entry;
    setStage(entry);
  }, []);

  const pushTcResult = useCallback(
    (result: TcLookupResultDto) => {
      tcSeqRef.current += 1;
      interrupt({
        source: 'tc',
        key: `tc|${tcSeqRef.current}`,
        result,
        at: new Date().toISOString(),
      });
    },
    [interrupt],
  );

  const pushVerifyResult = useCallback(
    (result: EntryVerifyResultDto) => {
      tcSeqRef.current += 1;
      interrupt({
        source: 'verify',
        key: `verify|${tcSeqRef.current}`,
        result,
        at: new Date().toISOString(),
      });
    },
    [interrupt],
  );

  const pushTcError = useCallback(() => {
    tcSeqRef.current += 1;
    interrupt({
      source: 'tcError',
      key: `tcError|${tcSeqRef.current}`,
      at: new Date().toISOString(),
    });
  }, [interrupt]);

  const head = queue[0];
  // Bir ağ hatası (`tcError`) ya da `found=false` bir TC sonucu NÖTRDÜR (bir ret DEĞİL,
  // `VerdictPanel.tsx`'in `toCardProps`'undaki AYNI ayrım) — yalnız gerçek bir izin/jeton reddi
  // (bkz. `isDenialEntry`) düğmeyi ikonlar.
  const nextIsDenial = head ? isDenialEntry(head) : false;

  return {
    stage,
    queuedCount: queue.length,
    nextIsDenial,
    acknowledge: advance,
    pushGateScans,
    pushTcResult,
    pushVerifyResult,
    pushTcError,
  };
}
